import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  UpdateDocumentAssessmentResultMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateDocumentAssessmentResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateDocumentAssessmentResultInput {
  Id: string;
  Rating?: InputMaybe<number>;
  Rationale?: InputMaybe<string>;
  TestDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

export const useUpdateDocumentAssessmentResult = createMutationHook({
  name: 'updateDocumentAssessmentResult',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.documentAssessmentResult.update.mutationOptions({
      onSuccess,
    }),
  mapInput: (input: UpdateDocumentAssessmentResultInput) => input,
  // Invalidation parity with the legacy Apollo evictFields:
  // document_assessment_result (+ aggregate), assessment,
  // internal_audit_report and oversight_assessment.
  invalidate: (trpc) => [
    trpc.frontend.documentAssessmentResult.pathKey(),
    trpc.frontend.assessment.pathKey(),
    trpc.frontend.internalAuditReport.pathKey(),
    trpc.frontend.oversight.pathKey(),
  ],
  // The tRPC update either succeeds on exactly one row or throws, so a
  // successful response always maps to affected_rows: 1.
  mapResponse: (): UpdateDocumentAssessmentResultMutation => ({
    update_document_assessment_result: {
      __typename: 'document_assessment_result_mutation_response',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateDocumentAssessmentResultDocument,
  graphqlVariables: (input: UpdateDocumentAssessmentResultInput) => ({
    ...input,
  }),
  evictFields: [
    'document_assessment_result',
    'assessment',
    'internal_audit_report',
    'oversight_assessment',
    'document_assessment_result_aggregate',
  ],
});
